import { useState, useEffect, useRef } from "react";
import { Search, Building2, User, Tag, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface Suggestion {
	type: string;
	label: string;
	sublabel: string;
	id: string;
}

export function SearchBar({
	onSearch,
	onSelect,
}: {
	onSearch: (query: string) => void;
	onSelect: (id: string, type: string) => void;
}) {
	const [query, setQuery] = useState("");
	const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [isLoading, setIsLoading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);

		if (query.trim().length < 1) {
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}

		setIsLoading(true);
		debounceRef.current = setTimeout(async () => {
			try {
				const res = await fetch(`/api/search/suggestions?q=${encodeURIComponent(query)}`);
				const data = await res.json();
				setSuggestions(data.suggestions);
				setShowSuggestions(data.suggestions.length > 0);
				setSelectedIndex(-1);
			} catch {
				setSuggestions([]);
			} finally {
				setIsLoading(false);
			}
		}, 150);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [query]);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setShowSuggestions(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setSelectedIndex((i) => Math.max(i - 1, -1));
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (selectedIndex >= 0 && suggestions[selectedIndex]) {
				const s = suggestions[selectedIndex];
				onSelect(s.id, s.type);
				setQuery(s.label);
				setShowSuggestions(false);
			} else {
				onSearch(query);
				setShowSuggestions(false);
			}
		} else if (e.key === "Escape") {
			setShowSuggestions(false);
		}
	}

	function getIcon(type: string) {
		switch (type) {
			case "company": return <Building2 className="w-4 h-4 text-blue-500" />;
			case "contact": return <User className="w-4 h-4 text-emerald-500" />;
			case "industry": return <Tag className="w-4 h-4 text-violet-500" />;
			default: return <Search className="w-4 h-4 text-muted-foreground" />;
		}
	}

	return (
		<div ref={containerRef} className="relative w-full max-w-2xl">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
				<Input
					ref={inputRef}
					type="text"
					placeholder="Search companies, contacts, industries..."
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						if (!e.target.value.trim()) onSearch("");
					}}
					onKeyDown={handleKeyDown}
					onFocus={() => {
						if (suggestions.length > 0) setShowSuggestions(true);
					}}
					className="pl-10 pr-10 h-12 text-base bg-white border-slate-200 shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-400 placeholder:text-slate-400"
				/>
				{query && (
					<button
						onClick={() => {
							setQuery("");
							onSearch("");
							inputRef.current?.focus();
						}}
						className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-muted-foreground"
					>
						<X className="w-4 h-4" />
					</button>
				)}
				{isLoading && (
					<div className="absolute right-10 top-1/2 -translate-y-1/2">
						<div className="w-4 h-4 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
					</div>
				)}
			</div>

			{showSuggestions && suggestions.length > 0 && (
				<div className="absolute z-50 w-full mt-1.5 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
					<div className="px-3 py-2 border-b border-slate-100">
						<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Suggestions</span>
					</div>
					{suggestions.map((s, i) => (
						<button
							key={`${s.type}-${s.id}-${i}`}
							className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
								i === selectedIndex ? "bg-blue-50" : "hover:bg-slate-50"
							}`}
							onClick={() => {
								onSelect(s.id, s.type);
								setQuery(s.label);
								setShowSuggestions(false);
							}}
							onMouseEnter={() => setSelectedIndex(i)}
						>
							<div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
								{getIcon(s.type)}
							</div>
							<div className="flex-1 min-w-0">
								<div className="text-sm font-medium text-foreground truncate">{s.label}</div>
								<div className="text-xs text-muted-foreground truncate">{s.sublabel}</div>
							</div>
							<span className="flex-shrink-0 text-[10px] font-medium text-muted-foreground uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded">
								{s.type}
							</span>
						</button>
					))}
					<div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50">
						<button
							className="text-xs text-blue-600 hover:text-blue-700 font-medium"
							onClick={() => {
								onSearch(query);
								setShowSuggestions(false);
							}}
						>
							Search all results for "{query}"
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
